// Auth client — the POST /v1/auth/login seam. Calls the live backend (same-origin,
// so the dev proxy / deploy forwards /v1), and returns the signed JWT from the
// envelope's data.access_token. The token is handed to the api-client via
// setAuthToken (see AuthProvider) so every subsequent request carries the bearer.

export interface Credentials {
  email: string;
  password: string;
}

export interface AuthToken {
  token: string;
}

export async function login(creds: Credentials): Promise<AuthToken> {
  if (!creds.email || !creds.password) {
    throw new Error("Enter your email and password.");
  }
  const res = await fetch("/v1/auth/login", {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ email: creds.email, password: creds.password }),
  });
  if (!res.ok) {
    // 401 is the ordinary wrong-credentials case: one generic sentence, matching the
    // server's own deliberately non-committal message. Never say which half was
    // wrong, and never attach a reference — there is nothing to investigate.
    if (res.status === 401) throw new Error("Incorrect email or password.");
    // 429 says how to recover, which the generic sentence did not. It leaks nothing:
    // the budget is keyed on the submitted address whether or not it exists, so the
    // response is identical either way.
    if (res.status === 429) {
      throw new Error("Too many sign-in attempts. Please wait a few minutes and try again.");
    }
    let message = res.status === 422
      ? "Check the email address and try again."
      : "Sign-in failed. Please try again.";
    try {
      const body = await res.json();
      // The API returns a `request_id` on every error and it was being thrown away,
      // so a user who called us could not quote anything and we could not trace the
      // report. Shown only for the UNEXPECTED statuses, where someone may actually
      // need to look it up.
      if (res.status >= 500 && typeof body?.request_id === "string" && body.request_id) {
        message = `${message} Reference ${body.request_id}.`;
      }
    } catch {
      /* non-JSON error body */
    }
    throw new Error(message);
  }
  const body = await res.json();
  const token = body?.data?.access_token;
  if (typeof token !== "string" || !token) {
    throw new Error("Sign-in failed: no token returned.");
  }
  return { token };
}
